<template>
    <div class="email-templates">
        <div v-if="!editMode">
            <!-- Şablon listesi -->
            <div class="toolbar-container">
                <div class="left-section">
                    <EmailTemplateFilterToolbar v-model="filters" ref="filterToolbar" />
                </div>
                <div class="right-section">
                    <SupsisButton @click="openCreateTemplateModal" primary small
                        >{{ $t("mail.newTemplate") }}
                    </SupsisButton>
                </div>
            </div>

            <supsis-table
                :columns="columns"
                :rows="templates"
                :bottom-pagination="true"
                :pagination-cursor="paginationCursor"
                @cursor-changed="onCursorChanged"
            >
            </supsis-table>

            <Modal2 v-if="showCreateTemplateModal" @cancel="closeCreateTemplateModal" @ok="createTemplate">
                <template #title>{{ $t("mail.newMailTemplateCreated") }}</template>
                <CreateEmailTemplateForm ref="createTemplateForm" />
            </Modal2>
        </div>

        <div v-else>
            <!-- Şablon editörü -->
            <EmailTemplateEditor
                :template="selectedTemplate"
                @close="editMode = false"
                @template-updated="onTemplateUpdated"
            />
        </div>
    </div>
</template>
<script>
import Vue from "vue";
import SupsisTable from "../../../../components/SupsisTable.vue";
import EmailTemplateFilterToolbar from "./EmailTemplateFilterToolbar.vue";
import { MAIL_API } from "../../../../api/mail";
import SupsisButton from "../../Settings/components/SupsisButton.vue";
import Modal2 from "../../../../components/common/Modal2.vue";
import CreateEmailTemplateForm from "./CreateEmailTemplateForm.vue";
import EmailTemplateEditor from "./EmailTemplateEditor.vue";

export default Vue.extend({
    name: "EmailTemplates",
    components: {
        SupsisTable,
        EmailTemplateFilterToolbar,
        SupsisButton,
        Modal2,
        CreateEmailTemplateForm,
        EmailTemplateEditor,
    },
    data() {
        return {
            templates: [],
            filters: [],
            paginationCursor: {
                total: 0,
                page: 1,
                perPage: 10,
            },
            columns: [
                {
                    key: "name",
                    field: "name",
                    title: this.$t("mail.templateName"),
                },
                {
                    key: "subject",
                    field: "subject",
                    title: this.$t("message.subject"),
                },
                {
                    key: "category",
                    field: "category",
                    title: this.$t("mail.category"),
                    renderBodyCell: ({ row }) => {
                        console.log("renderBodyCell", row);
                        const colors = {
                            marketing: "blue",
                            operational: "green",
                            private: "red",
                        };
                        const color = colors[row.category] || "gray";
                        return (
                            <span class="category-chip" style={{ backgroundColor: color }}>
                                {row.category}
                            </span>
                        );
                    },
                },
                {
                    key: "creator",
                    field: "creator",
                    title: this.$t("message.createdAt"),
                    renderBodyCell: ({ row }) => row.creator?.name,
                },
                {
                    key: "createdAt",
                    field: "createdAt",
                    title: this.$t("ticket.ticketFilterList.creationDate"),
                    renderBodyCell: ({ row }) => new Date(row.createdAt).toLocaleDateString(),
                },
                {
                    key: "actions",
                    title: this.$t("message.transactions"),
                    renderBodyCell: ({ row }) => {
                        return (
                            <div class="actions d-flex gap-5">
                                <SupsisButton small onClick={() => this.editTemplate(row)}>
                                    {this.$t("message.edit")}
                                </SupsisButton>

                                <SupsisButton small type="danger" onClick={() => this.deleteTemplate(row._id)}>
                                    {this.$t("message.delete")}
                                </SupsisButton>
                            </div>
                        );
                    },
                },
            ],
            showCreateTemplateModal: false,
            editMode: false,
            selectedTemplate: null,
        };
    },
    methods: {
        async fetch() {
            const { templates, total } = await MAIL_API.TEMPLATE.listEmailTemplates({
                filters: this.filters,
                cursor: {
                    page: this.paginationCursor.page,
                    perPage: this.paginationCursor.perPage,
                },
            });
            this.templates = templates;
            this.paginationCursor.total = total;
        },
        async onCursorChanged(newCursor) {
            this.paginationCursor = newCursor;
            await this.fetch();
        },
        refreshData() {
            this.fetch();
        },
        editTemplate(template) {
            MAIL_API.TEMPLATE.getEmailTemplateById(template._id).then((response) => {
                this.selectedTemplate = response;
                this.editMode = true;
            });
        },
        deleteTemplate(templateId) {
            this.$dialog
                .confirm("Are you sure you want to delete this template?", {
                    okText: "Yes",
                    cancelText: "No",
                    customClass: "supsis-dialog",
                })
                .then(() => {
                    this.paginationCursor.page = 1;
                    MAIL_API.TEMPLATE.deleteMailTemplate(templateId)
                        .then(() => {
                            this.$message.success("Mail template deleted successfully.");
                            this.fetch();
                        })
                        .catch((error) => {
                            this.$message.error("An error occurred while deleting the mail template.");
                        });
                });
        },
        openCreateTemplateModal() {
            this.showCreateTemplateModal = true;
        },
        closeCreateTemplateModal() {
            this.showCreateTemplateModal = false;
        },
        async createTemplate() {
            const { name, subject, category, template } = this.$refs.createTemplateForm.getFormData();
            await MAIL_API.TEMPLATE.createMailTemplate({
                name,
                subject,
                category,
                template,
                userId: this.$store.state.auth.user._id,
            });
            this.closeCreateTemplateModal();
            this.fetch();
        },
        onTemplateUpdated(updatedTemplate) {
            const index = this.templates.findIndex((template) => template.id === updatedTemplate.id);
            if (index !== -1) {
                this.$set(this.templates, index, updatedTemplate);
            }
        },
    },
    watch: {
        filters: {
            handler() {
                this.paginationCursor.page = 1;
                this.fetch();
            },
            deep: true,
        },
    },
    mounted() {
        this.fetch();
    },
});
</script>

<style scoped lang="scss">
.email-templates {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 10px;
}

.category-chip {
    display: inline-block;
    padding: 4px 8px;
    border-radius: 20px;
    color: white;
    font-size: 12px;
    font-weight: bold;
}

.toolbar-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;

    .left-section {
        flex: 1;
    }

    .right-section {
        display: flex;
        justify-content: flex-end;
    }
}
</style>

<style lang="scss">
.supsis-dialog {
    .dg-content-footer {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 5px;

        .dg-btn--cancel {
            color: #22272d;
            background-color: #fefefe;
            border-color: #22272d;
        }

        .dg-btn--ok {
            color: #fff;
            background-color: #22272d;
            border-color: #22272d;
        }
    }
}
</style>
